import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Shield, 
  Clock, 
  HelpCircle, 
  CheckCircle, 
  Upload, 
  Lock,
  ChevronDown,
  ArrowRight,
  Home,
  Building2,
  LandPlot,
  Hammer,
  Mail,
  AlertTriangle,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ContractWiserLogo, ContractWiserLogoIcon } from "@/components/ContractWiserLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const navigate = useNavigate();
  const [showUnsupportedModal, setShowUnsupportedModal] = useState(false);

  useEffect(() => {
    document.title = "ContractWiser | Australian Property Contract Analysis";
  }, []);

  const features = [
    {
      icon: FileText,
      title: "Contract Snapshot",
      description: "Key details at a glance: price, deposit, settlement date, and cooling-off period."
    },
    {
      icon: AlertTriangle,
      title: "What Matters Most",
      description: "Top clauses you need to review carefully before signing."
    },
    {
      icon: HelpCircle,
      title: "Special Conditions",
      description: "Plain-English explanations of all special conditions in your contract."
    },
    {
      icon: CheckCircle,
      title: "Things to Clarify",
      description: "Unusual items and potential concerns you should confirm."
    },
    {
      icon: Shield,
      title: "Questions for Your Solicitor",
      description: "Practical, contract-specific questions to ask during your meeting."
    }
  ];

  const targetAudience = [
    { icon: Home, label: "First-time buyers", desc: "Navigating your first property purchase" },
    { icon: Building2, label: "Property investors", desc: "Adding to your portfolio" },
    { icon: LandPlot, label: "Land buyers", desc: "Purchasing vacant land" },
    { icon: Hammer, label: "Off-the-plan buyers", desc: "Buying before construction" }
  ];

  const faqs = [
    {
      question: "What states are supported?",
      answer: "We support property contracts from all Australian states and territories — NSW, VIC, QLD, SA, WA, TAS, ACT, and NT.",
      hasContactLink: true
    },
    {
      question: "What type of contracts can I upload?",
      answer: "We analyze residential property contracts including houses, units/apartments, vacant land, and off-the-plan purchases. Commercial property contracts are not currently supported."
    },
    {
      question: "Is this legal advice?",
      answer: "No. This is an educational summary to help you understand your contract before speaking with your solicitor. It does not replace professional legal advice. Always consult a qualified solicitor before signing any property contract."
    },
    {
      question: "How long does the analysis take?",
      answer: "Most contracts are analyzed within 2-5 minutes. Complex contracts may take slightly longer. You'll receive your report on screen immediately after analysis is complete."
    },
    {
      question: "What happens to my uploaded contract?",
      answer: "Your document is used only to generate your report and is automatically deleted after processing. We do not store, share, or retain your contract files."
    },
    {
      question: "Can I get a refund?",
      answer: "If our system fails to analyze your contract due to a technical issue, we'll process a full refund. Please contact us at admin@contractwiser.com.au with your receipt."
    }
  ];

  const [showMoreSample, setShowMoreSample] = useState(false);

  const sampleReport = {
    contract_snapshot: {
      address: "42 Harbour View Drive, Sydney NSW 2000",
      purchase_price: "$1,250,000",
      deposit: "$125,000 (10%) - Due within 5 business days",
      settlement_date: "42 days from exchange",
      cooling_off: "5 business days (standard NSW)"
    },
    matters_most: [
      { clause: "Sunset Clause (Clause 28)", explanation: "Developer can rescind if not settled by Dec 2026. You could lose your deposit if delays occur.", importance: "high" },
      { clause: "Defects Liability Period", explanation: "Only 90 days to report defects - most issues appear after 6+ months. This is shorter than standard.", importance: "high" },
      { clause: "Builder's Margin Clause", explanation: "20% margin on variations - significantly higher than industry standard 10-15%.", importance: "high" }
    ],
    special_conditions: [
      { condition: "Special Condition 4: No Cooling-Off Waiver", explanation: "Buyer waives cooling-off rights if exchanging at auction or with 66W certificate. Once signed, you cannot back out.", concern: "high" },
      { condition: "Special Condition 7: Strata Levies Adjustment", explanation: "You're responsible for any special levy passed before settlement, even if you didn't vote on it. Could be $10,000+.", concern: "medium" },
      { condition: "Special Condition 12: Early Access Denied", explanation: "No access for measurements or inspections before settlement. You can't check the property condition.", concern: "medium" }
    ],
    things_to_clarify: [
      { item: "Easement on Title (DP 1234567)", reason: "An easement for drainage runs through the backyard. This may restrict future renovations or pool installation." },
      { item: "Section 149 Certificate Missing", reason: "Zoning certificate not attached. Cannot verify permitted land uses or future development plans nearby." },
      { item: "Body Corporate Financials", reason: "No sinking fund statement provided. Building may have insufficient funds for major repairs." }
    ],
    questions: [
      "Can the sunset clause date be extended, and what happens to my deposit if the developer exercises this clause?",
      "Are there any pending or proposed special levies I should know about?",
      "Can the defects liability period be extended to the standard 2 years?",
      "What is the current sinking fund balance and are there any planned capital works?",
      "Has the property been subject to any insurance claims in the past 5 years?"
    ]
  };

  return (
    <div className="min-h-screen bg-background relative">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <ContractWiserLogo size="default" />
          <div className="flex items-center gap-4">
            <span className="hidden md:inline text-sm text-muted-foreground">
              All Australian States & Territories
            </span>
            <Button 
              data-testid="nav-upload-btn"
              onClick={() => navigate('/upload')}
              className="bg-eucalypt-700 hover:bg-eucalypt-800 text-white"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Contract
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1767680675696-e065b32dac89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhdXN0cmFsaWFuJTIwaG9tZSUyMGV4dGVyaW9yJTIwc3VubGlnaHR8ZW58MHx8fHwxNzczNDg2NDk5fDA&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-eucalypt-900/85"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-terracotta-400 font-body text-sm font-medium tracking-wide uppercase mb-4">
              Australian Property Contract Analysis
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Understand your property contract before speaking to your solicitor
            </h1>
            <p className="text-sandstone-300 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl">
              Don't sign what you don't understand. Upload your property contract and get a report with key clauses explained, what matters and what to clarify with your solicitor to avoid costly surprises.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                data-testid="hero-upload-btn"
                size="lg"
                onClick={() => navigate('/upload')}
                className="bg-terracotta-600 hover:bg-terracotta-700 text-white text-lg px-8 py-6 shadow-glow"
              >
                <Upload className="h-5 w-5 mr-2" />
                Upload Contract
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button 
                data-testid="hero-sample-btn"
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('sample-report').scrollIntoView({ behavior: 'smooth' })}
                className="border-sandstone-400 text-sandstone-100 hover:bg-sandstone-100/10 text-lg px-8 py-6"
              >
                See Sample Report
              </Button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sandstone-400 text-sm">
              <span className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Files deleted after processing
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Results in ~3 minutes
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="bg-eucalypt-800 py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sandstone-200 text-sm">
            <span className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-terracotta-400" />
              Built for Australian property buyers
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-terracotta-400" />
              Educational summary only, not legal advice
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-terracotta-400" />
              No account required
            </span>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-eucalypt-800 mb-4">
              What You Get
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A comprehensive, easy-to-understand report covering everything you need to know before your solicitor meeting.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 shadow-card hover:shadow-float transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-eucalypt-100 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-eucalypt-700" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-eucalypt-800 mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 md:py-28 bg-sandstone-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-eucalypt-800 mb-4">
              Who It's For
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Whether you're buying your first home or your tenth investment property, we help you understand your contract.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {targetAudience.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 text-center shadow-card hover:shadow-float transition-shadow duration-300"
              >
                <div className="w-16 h-16 bg-terracotta-100 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-8 w-8 text-terracotta-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-eucalypt-800 mb-1">
                  {item.label}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Report Preview */}
      <section id="sample-report" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-eucalypt-800 mb-4">
              Sample Report Preview
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              See how we highlight costly clauses that solicitors often gloss over.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-white shadow-float overflow-hidden">
              {/* Report Header */}
              <div className="bg-eucalypt-800 px-8 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="h-6 w-6 text-sandstone-200" />
                  <span className="text-sandstone-200 font-body text-sm uppercase tracking-wide">
                    Contract Analysis Report
                  </span>
                </div>
                <h3 className="font-heading text-2xl text-white">
                  {sampleReport.contract_snapshot.address}
                </h3>
              </div>

              {/* Contract Snapshot */}
              <div className="p-8 border-b border-sandstone-200">
                <h4 className="font-heading text-xl text-eucalypt-800 mb-6 flex items-center gap-2">
                  <span className="w-8 h-8 bg-eucalypt-100 flex items-center justify-center text-eucalypt-700 font-bold text-sm">1</span>
                  Contract Snapshot
                </h4>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Purchase Price</p>
                    <p className="text-2xl font-heading font-semibold text-eucalypt-800">{sampleReport.contract_snapshot.purchase_price}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Deposit</p>
                    <p className="text-lg font-body text-eucalypt-700">{sampleReport.contract_snapshot.deposit}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Settlement</p>
                    <p className="text-lg font-body text-eucalypt-700">{sampleReport.contract_snapshot.settlement_date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Cooling-Off Period</p>
                    <p className="text-lg font-body text-eucalypt-700">{sampleReport.contract_snapshot.cooling_off}</p>
                  </div>
                </div>
              </div>

              {/* What Matters Most - High Cost Clauses */}
              <div className="p-8 bg-terracotta-50/50 border-b border-sandstone-200">
                <h4 className="font-heading text-xl text-eucalypt-800 mb-6 flex items-center gap-2">
                  <span className="w-8 h-8 bg-terracotta-100 flex items-center justify-center text-terracotta-700 font-bold text-sm">2</span>
                  What Matters Most
                  <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-1 rounded">High Cost Risk</span>
                </h4>
                <div className="space-y-4">
                  {sampleReport.matters_most.map((item, index) => (
                    <div key={index} className="bg-white p-4 border-l-4 border-terracotta-500">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-terracotta-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-eucalypt-800">{item.clause}</p>
                          <p className="text-muted-foreground text-sm mt-1">{item.explanation}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Special Conditions - Often Missed */}
              <div className="p-8 border-b border-sandstone-200">
                <h4 className="font-heading text-xl text-eucalypt-800 mb-6 flex items-center gap-2">
                  <span className="w-8 h-8 bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm">3</span>
                  Special Conditions
                  <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">Often Overlooked</span>
                </h4>
                <div className="space-y-3">
                  {sampleReport.special_conditions.slice(0, 2).map((item, index) => (
                    <div key={index} className={`p-4 border-l-4 ${item.concern === 'high' ? 'bg-red-50 border-red-500' : 'bg-amber-50 border-amber-500'}`}>
                      <p className="font-semibold text-eucalypt-800">{item.condition}</p>
                      <p className="text-muted-foreground text-sm mt-1">{item.explanation}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Expandable Section */}
              {showMoreSample && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                >
                  {/* More Special Conditions */}
                  <div className="p-8 border-b border-sandstone-200 bg-sandstone-50">
                    <div className="space-y-3">
                      {sampleReport.special_conditions.slice(2).map((item, index) => (
                        <div key={index} className="p-4 bg-white border-l-4 border-amber-500">
                          <p className="font-semibold text-eucalypt-800">{item.condition}</p>
                          <p className="text-muted-foreground text-sm mt-1">{item.explanation}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Things to Clarify */}
                  <div className="p-8 border-b border-sandstone-200">
                    <h4 className="font-heading text-xl text-eucalypt-800 mb-6 flex items-center gap-2">
                      <span className="w-8 h-8 bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">4</span>
                      Things to Clarify
                    </h4>
                    <div className="space-y-3">
                      {sampleReport.things_to_clarify.map((item, index) => (
                        <div key={index} className="p-4 bg-blue-50 border-l-4 border-blue-500">
                          <p className="font-semibold text-eucalypt-800 flex items-center gap-2">
                            <HelpCircle className="h-4 w-4 text-blue-600" />
                            {item.item}
                          </p>
                          <p className="text-muted-foreground text-sm mt-1 pl-6">{item.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Questions for Solicitor */}
                  <div className="p-8 bg-eucalypt-50">
                    <h4 className="font-heading text-xl text-eucalypt-800 mb-6 flex items-center gap-2">
                      <span className="w-8 h-8 bg-eucalypt-200 flex items-center justify-center text-eucalypt-700 font-bold text-sm">5</span>
                      Questions for Your Solicitor
                    </h4>
                    <ol className="space-y-3">
                      {sampleReport.questions.map((question, index) => (
                        <li key={index} className="flex items-start gap-3 bg-white p-3 rounded">
                          <span className="w-6 h-6 bg-eucalypt-200 flex items-center justify-center text-eucalypt-700 font-semibold text-sm flex-shrink-0 rounded">
                            {index + 1}
                          </span>
                          <span className="text-eucalypt-700">{question}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </motion.div>
              )}

              {/* Expand/Collapse Button */}
              <div className="p-6 bg-gradient-to-t from-sandstone-100 to-white flex flex-col items-center gap-4">
                <Button 
                  variant="outline"
                  onClick={() => setShowMoreSample(!showMoreSample)}
                  className="border-eucalypt-300 text-eucalypt-700"
                >
                  {showMoreSample ? (
                    <>
                      <ChevronDown className="h-4 w-4 mr-2 rotate-180" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-2" />
                      See Full Report Sample
                    </>
                  )}
                </Button>
                <Button 
                  data-testid="sample-upload-btn"
                  onClick={() => navigate('/upload')}
                  className="bg-terracotta-600 hover:bg-terracotta-700 text-white"
                >
                  Get Your Full Report
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 md:py-28 bg-eucalypt-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              Simple, One-Time Pricing
            </h2>
            <p className="text-sandstone-300 text-lg max-w-2xl mx-auto mb-12">
              No subscriptions. No hidden fees. Just pay once per contract.
            </p>

            <div className="max-w-md mx-auto bg-white p-10 shadow-float">
              <div className="mb-6">
                <span className="text-6xl font-heading font-bold text-eucalypt-800">$49.99</span>
                <span className="text-muted-foreground ml-2">AUD</span>
              </div>
              <p className="text-eucalypt-700 font-body mb-8">
                Per contract analysis
              </p>
              <ul className="text-left space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-eucalypt-600" />
                  <span>Complete 5-section report</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-eucalypt-600" />
                  <span>Plain-English explanations</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-eucalypt-600" />
                  <span>Questions for your solicitor</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-eucalypt-600" />
                  <span>Instant results</span>
                </li>
                <li className="flex items-center gap-3">
                  <Lock className="h-5 w-5 text-eucalypt-600" />
                  <span>Secure & confidential</span>
                </li>
              </ul>
              <Button 
                data-testid="pricing-upload-btn"
                size="lg"
                onClick={() => navigate('/upload')}
                className="w-full bg-terracotta-600 hover:bg-terracotta-700 text-white text-lg py-6"
              >
                Upload Your Contract
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Security & Privacy */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-16 h-16 bg-eucalypt-100 flex items-center justify-center mb-6">
                <Shield className="h-8 w-8 text-eucalypt-700" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-eucalypt-800 mb-4">
                Your Privacy Matters
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                We understand you're uploading sensitive legal documents. That's why we take security seriously.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-eucalypt-600 mt-1" />
                  <div>
                    <span className="font-semibold text-eucalypt-800">Automatic deletion</span>
                    <p className="text-muted-foreground text-sm">Your contract is deleted immediately after analysis</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-eucalypt-600 mt-1" />
                  <div>
                    <span className="font-semibold text-eucalypt-800">Encrypted transfer</span>
                    <p className="text-muted-foreground text-sm">All uploads use secure, encrypted connections</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-eucalypt-600 mt-1" />
                  <div>
                    <span className="font-semibold text-eucalypt-800">No data retention</span>
                    <p className="text-muted-foreground text-sm">We don't store, share, or sell your documents</p>
                  </div>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1764087957302-ef0756ed8e0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxoYW5kcyUyMHNpZ25pbmclMjBwYXBlciUyMGRvY3VtZW50JTIwY2xvc2UlMjB1cCUyMGx1eHVyeSUyMHBlbnxlbnwwfHx8fDE3NzM0ODY1MTB8MA&ixlib=rb-4.1.0&q=85"
                alt="Signing contract with luxury pen"
                className="w-full h-auto shadow-float"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-sandstone-100">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-eucalypt-800 mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white px-6 shadow-card"
              >
                <AccordionTrigger 
                  data-testid={`faq-trigger-${index}`}
                  className="font-heading text-left text-eucalypt-800 hover:no-underline"
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {faq.hasContactLink ? (
                    <>
                      We support property contracts from all Australian states and territories — NSW, VIC, QLD, SA, WA, TAS, ACT, and NT.
                    </>
                  ) : (
                    faq.answer
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-eucalypt-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center">
            <p className="text-sandstone-400 text-sm leading-relaxed max-w-3xl mx-auto">
              <strong className="text-sandstone-200">Disclaimer:</strong> ContractWiser provides educational summaries of property contracts to help buyers prepare for solicitor discussions. 
              This is not legal advice and does not replace consultation with a qualified solicitor or conveyancer. 
              Always seek independent legal advice before signing any property contract.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-eucalypt-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <ContractWiserLogoIcon size={28} className="opacity-80" />
              <span className="font-heading text-lg text-sandstone-200">ContractWiser</span>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-6 text-sandstone-400 text-sm">
              <button onClick={() => navigate('/terms')} className="hover:text-sandstone-200 transition-colors">
                Terms of Service
              </button>
              <button onClick={() => navigate('/privacy')} className="hover:text-sandstone-200 transition-colors">
                Privacy Policy
              </button>
              <button onClick={() => navigate('/contact')} className="hover:text-sandstone-200 transition-colors">
                Contact Us
              </button>
              <a href="mailto:admin@contractwiser.com.au" className="hover:text-sandstone-200 transition-colors flex items-center gap-1">
                <Mail className="h-4 w-4" />
                admin@contractwiser.com.au
              </a>
            </div>
            
            <p className="text-sandstone-500 text-xs">
              © 2026 ContractWiser
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
